import React, { useRef, useState } from "react";
import { Box, Typography, FormLabel, Alert, Stack } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import { useToast } from "../toast";
import { formatFileSize } from "../../services/Utils";

export default function CognitoAiFileUploadPickerPane({
  onFileSelected,
  resumeUpload = null,
}) {
  const theme = useTheme();
  const filePickerInput = useRef();
  const { addToast } = useToast();
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState("");

  const ALLOWED_EXTENSIONS = ["tar", "gz", "tgz", "iso"];
  const MAX_FILE_SIZE = 7 * 1024 * 1024 * 1024; // 7GB

  const getFileType = (name) => {
    const n = name.toLowerCase();
    if (n.endsWith(".tar.gz") || n.endsWith(".tgz")) return "application/gzip";
    if (n.endsWith(".gz")) return "application/gzip";
    if (n.endsWith(".tar")) return "application/x-tar";
    if (n.endsWith(".iso")) return "application/x-iso9660-image";
    return "application/octet-stream";
  };

  const validateFilePicked = (event) => {
    const selectedFile = event?.target?.files[0];

    if (!selectedFile) return;

    // Validate file extension
    const fileName = selectedFile.name.toLowerCase();
    const ext = fileName.split(".").pop();

    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      addToast({
        type: "error",
        message: `Invalid file type. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}`,
        autoClose: 3000,
      });
      filePickerInput.current.value = null;
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      addToast({
        type: "error",
        message: `File size exceeds maximum allowed (7GB)`,
        autoClose: 3000,
      });
      filePickerInput.current.value = null;
      return;
    }

    if (selectedFile.size < 1024) {
      addToast({
        type: "error",
        message: `File size is too small (minimum 1KB)`,
        autoClose: 3000,
      });
      filePickerInput.current.value = null;
      return;
    }

    // Reupload validation: file name, size, and type must match the original
    if (resumeUpload) {
      const errors = [];
      const originalName = resumeUpload.fileName || "";
      const originalSize = parseInt(resumeUpload.fileSize) || 0;
      const originalType = resumeUpload.fileType || getFileType(originalName);
      const selectedType = selectedFile.type || getFileType(selectedFile.name);

      if (selectedFile.name !== originalName) {
        errors.push(
          `File name mismatch: expected "${originalName}", got "${selectedFile.name}"`,
        );
      }
      if (selectedFile.size !== originalSize) {
        errors.push(
          `File size mismatch: expected ${formatFileSize(originalSize)}, got ${formatFileSize(selectedFile.size)}`,
        );
      }
      if (selectedType !== originalType) {
        errors.push(
          `File type mismatch: expected "${originalType}", got "${selectedType}"`,
        );
      }

      setSelectedFileName(selectedFile.name);

      if (errors.length > 0) {
        setValidationErrors(errors);
        filePickerInput.current.value = null;
        return;
      }
      setValidationErrors([]);
    }

    onFileSelected(selectedFile);
    event.target.value = "";
  };

  return (
    <div>
      <Typography sx={{ fontSize: 15, marginBottom: 2 }} color="text.primary">
        Select a Cognito AI image file to upload
      </Typography>

      {resumeUpload && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Reupload — the selected file must exactly match the original:
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              • Name: <strong>{resumeUpload.fileName}</strong>
            </Typography>
            <Typography variant="body2">
              • Size:{" "}
              <strong>{formatFileSize(parseInt(resumeUpload.fileSize))}</strong>
            </Typography>
            <Typography variant="body2">
              • Type: <strong>{resumeUpload.fileType}</strong>
            </Typography>
          </Stack>
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            File does not match the original upload:
          </Typography>
          <Stack spacing={0.5}>
            {validationErrors.map((err, i) => (
              <Typography key={i} variant="body2">
                • {err}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          top: 0,
          left: 0,
          height: "400px",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed",
          borderColor: validationErrors.length > 0 ? "error.main" : grey[500],
          background: grey[200],
        }}
      >
        <FormLabel
          htmlFor="cognito-ai-file-upload"
          sx={{
            textAlign: "center",
            fontSize: 30,
            padding: 2,
            width: 300,
            borderRadius: 2,
            border: "1px solid white",
            cursor: "pointer",
            color: "#fff",
            background: theme.palette.primary[700],
            boxShadow: 10,
          }}
        >
          Upload
        </FormLabel>
      </Box>

      <Box sx={{ display: "none" }}>
        <input
          type="file"
          id="cognito-ai-file-upload"
          ref={filePickerInput}
          onInput={(e) => validateFilePicked(e)}
          accept=".tar,.gz,.tgz,.iso"
        />
      </Box>

      <Typography sx={{ fontSize: 12, marginTop: 2, color: "text.secondary" }}>
        Supported formats: TAR, GZ, TGZ, ISO | Max size: 7GB
      </Typography>
    </div>
  );
}
